/**
 * DepartmentDetails — a department as a canvas with panes over its edges.
 *
 * The same change as `Teams/TeamDetail`, because this was the same file: a
 * fixed `lg:grid-cols-3` whose right column held a hand-rolled Members card
 * with a thirty-line side-panel payload inline in its Button's `onClick`, a
 * `RecentActivityCard` showing a truncated copy of the audit log, and an
 * `ActivityDrawer` — a `fixed inset-y-0 right-0` panel with its own scrim —
 * showing the whole of it.
 *
 * Context, members and the log are PANES now. The bodies are the team's:
 * `Teams/surface/OrgContextPane`, the shared `AccessPane`, `AuditLogPanel`.
 * Only the surface (its storage key, its labels) and the domain thunks are a
 * department's own. See `surface/layout`.
 */

import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useAppDispatch } from '../../../store';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { useTranslation } from 'react-i18next';
import { hideModal, showModal } from '../../../store/actions/modalActions';
import { showSidePanel, hideSidePanel } from '../../../store/actions/sidePanelActions';
import { showFeedbackModal } from '../../../store/actions/UserFeedbackActions';
import apis from '../../../utils/apis';
import {
  PiArrowLeftLight,
  PiPencilLight,
  PiTrashLight,
  PiPlusCircleLight,
  PiTextAlignLeftLight,
  PiCircleDashedLight,
  PiUsersLight,
  PiCalendarBlankLight,
  PiClockCounterClockwiseLight,
  PiTagLight,
  PiInfoLight,
  PiFileTextLight,
} from 'react-icons/pi';
import Button from '../../shared/TailwindComponents/Button';
import Badge from '../../shared/TailwindComponents/Badge';
import Spinner from '../../shared/TailwindComponents/Spinner';
import Skeleton from '../../shared/TailwindComponents/Skeleton';
import GlassCard from '../../shared/brand/GlassCard';
import { FactTile, FieldGrid } from '../../shared/brand/Field';
import Input from '../../shared/TailwindComponents/Input';
import Textarea from '../../shared/TailwindComponents/Textarea';
import Dropdown from '../../shared/TailwindComponents/Dropdown';
import { LayoutContext } from '../../../contexts/LayoutContext';
import { fetchPracticeAreas } from '../../../store/actions/DropdownActions';
import {
  fetchDepartmentDetails,
  updateDepartment,
  addDepartmentMember,
  removeDepartmentMember,
  fetchDepartmentFeed,
  postDepartmentFeedEvent,
  deleteDepartment,
} from '../../../store/actions/organization/DepartmentActions';
import { ActivityFeed } from '../../shared/feed';
import DetailFrame from '../../shared/Frames/DetailFrame';
import DetailTabBar, { TabItem } from '../../shared/surface/DetailTabBar';
import DetailPageSurface from '../../shared/surface/DetailPageSurface';
import PaneRail from '../../shared/surface/PaneRail';
import PaneBody from '../../shared/surface/PaneBody';
import DocumentsTab from '../Shared/DocumentsTab';
import OrgReaderPane from '../Shared/OrgReaderPane';
import { OrgSurfaceProvider } from '../Shared/OrgSurfaceContext';
import { formatDate } from '../../../utils/functions';
import AuditLogPanel from '../../AuditLog/AuditLogPanel';
import AccessPane from '../../shared/access/AccessPane';
import CollaboratorStack from '../../shared/access/CollaboratorStack';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, departmentDetailTourSteps } from '../../shared/FeatureTour';
import OrgContextPane from '../Teams/surface/OrgContextPane';
import { orgAccessPanelPayload, toAccessRows } from '../Teams/surface/orgMembers';
import { DEPARTMENT_SURFACE, DEPARTMENT_TABS, useDepartmentWorkspace } from './surface';

interface Department {
  id: string;
  name: string;
  description?: string;
  is_active: boolean;
  member_count: number;
  members: Array<{
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    role?: string;
    joined_at?: string;
    profile_picture?: string | null;
  }>;
  created_at?: string;
  practice_area?: string;
  practice_areas?: Array<{ id: number; name: string }>;
  practice_area_ids?: number[];
  document_count?: number;
}

interface DepartmentDetailParams {
  departmentId: string;
}

const DepartmentDetail: React.FC = () => {
  const { t } = useTranslation();
  const { departmentId } = useParams<DepartmentDetailParams>();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { setIsContentScrollable } = useContext(LayoutContext);
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const practiceAreas = useSelector((state: RootState) => state.dropdown.dropdowns.practiceAreas) || [];

  const [department, setDepartment] = useState<Department | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [feed, setFeed] = useState<any[]>([]);
  const [feedLoading, setFeedLoading] = useState(false);
  const [documents, setDocuments] = useState<any[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [isUploadingDocs, setIsUploadingDocs] = useState(false);
  const [uploadingDocCount, setUploadingDocCount] = useState(0);
  const [isEditingDeptInfo, setIsEditingDeptInfo] = useState(false);
  const [editedDeptFields, setEditedDeptFields] = useState<any>({});
  const [isSaving, setIsSaving] = useState(false);

  const {
    panels,
    api: surfaceApi,
    activeTab,
    goToTab,
    readerContent,
    setReaderContent,
  } = useDepartmentWorkspace(departmentId);

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'department-detail',
    steps: departmentDetailTourSteps,
    readyToShow: !loading && !!department,
    autoStartDelay: 600,
  });

  useEffect(() => {
    setIsContentScrollable(false);
    return () => setIsContentScrollable(true);
  }, [setIsContentScrollable]);

  useEffect(() => {
    dispatch(fetchPracticeAreas());
  }, [dispatch]);

  const practiceAreaOptions = practiceAreas.map((area: any) => ({
    value: String(area.value || area.id),
    label: String(area.label || area.name),
  }));

  const loadDepartment = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await dispatch(fetchDepartmentDetails(departmentId) as any);
      if (result) {
        setDepartment(result);
        setEditedDeptFields(result);
      } else {
        setError(t('Failed to load department'));
      }
    } catch (err) {
      setError(t('Failed to load department'));
    } finally {
      setLoading(false);
    }
  }, [departmentId, dispatch, t]);

  /**
   * NEWEST FIRST. The department feed endpoint answers oldest-first, unlike
   * the team's — which is why `handleAddComment` below has to map a displayed
   * index back onto the original one.
   */
  const loadFeed = useCallback(async () => {
    setFeedLoading(true);
    try {
      const result = await dispatch(fetchDepartmentFeed(departmentId) as any);
      if (Array.isArray(result)) setFeed(result.reverse());
    } finally {
      setFeedLoading(false);
    }
  }, [departmentId, dispatch]);

  const loadDocuments = useCallback(async () => {
    setDocumentsLoading(true);
    try {
      const response = await apis.get(`/org-people/departments/${departmentId}/documents`);
      setDocuments(Array.isArray(response.data.documents) ? response.data.documents : []);
    } catch (err) {
      console.error('Error loading documents:', err);
    } finally {
      setDocumentsLoading(false);
    }
  }, [departmentId]);

  useEffect(() => {
    if (!departmentId) return;
    loadDepartment();
    loadFeed();
  }, [departmentId, loadDepartment, loadFeed]);

  useEffect(() => {
    if (activeTab === 'Documents') loadDocuments();
    if (activeTab === 'Feed') loadFeed();
  }, [activeTab, loadDocuments, loadFeed]);

  const handleDeptFieldChange = (field: string, value: any) => {
    setEditedDeptFields((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleSaveDepartment = async () => {
    if (!department?.id) return;
    setIsSaving(true);
    try {
      await dispatch(updateDepartment(department.id, {
        name: editedDeptFields.name,
        description: editedDeptFields.description,
        practice_area_ids: (editedDeptFields.practice_area_ids || []).map(Number),
      }));
      setIsEditingDeptInfo(false);
      await loadDepartment();
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Department updated successfully'),
        modalType: 'success',
        duration: 3000,
      }));
    } catch (err) {
      console.error('Error saving department:', err);
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Failed to save department'),
        modalType: 'error',
        duration: 3000,
      }));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingDeptInfo(false);
    setEditedDeptFields(department || {});
  };

  /** One call into the shared builder — see `Teams/surface/orgMembers`. */
  const handleManageAccess = useCallback(() => {
    dispatch(showSidePanel({
      ...orgAccessPanelPayload({
        resourceUuid: departmentId,
        resourceType: 'department',
        members: department?.members,
        title: t('Manage Members'),
        subMsg: t('Add or remove members of this department.'),
        icon: <PiUsersLight className="w-5 h-5" />,
        currentUserUuid: userInfo?.UserID ? String(userInfo.UserID) : undefined,
        addMember: (uuid, userUuid) => dispatch(addDepartmentMember(uuid, userUuid, 'member') as any),
        removeMember: (uuid, collabUuid) => dispatch(removeDepartmentMember(uuid, collabUuid) as any),
        onUpdate: () => { loadDepartment(); },
      }),
      onCancel: () => dispatch(hideSidePanel()),
    } as any));
  }, [dispatch, departmentId, department, userInfo, loadDepartment, t]);

  const handlePostFeedEvent = async (eventData: { event_type: string; message: string; metadata?: any }) => {
    try {
      await dispatch(postDepartmentFeedEvent(departmentId, eventData));
      await loadFeed();
    } catch (err) {
      console.error('Error posting feed event:', err);
    }
  };

  const handleAddComment = async (eventId: string, comment: string) => {
    try {
      if (!department?.id) throw new Error('Department ID not available');
      // The list is displayed reversed (see `loadFeed`), and the endpoint
      // addresses an event by its index in the ORIGINAL order.
      const displayedIndex = parseInt(eventId, 10);
      const originalIndex = feed.length - 1 - displayedIndex;
      await apis.post(
        `/org-people/departments/${department.id}/feed/${originalIndex}/comments`,
        { message: comment }
      );
      await loadFeed();
    } catch (err) {
      console.error('Error adding comment:', err);
      const errorMessage = err instanceof Error ? err.message : t('Unknown error');
      dispatch(showFeedbackModal({
        showModal: true,
        message: `${t('Comment failed')}: ${errorMessage}`,
        modalType: 'error',
        duration: 5000,
      }));
    }
  };

  const handleDeleteDepartment = () => {
    dispatch(showModal({
      type: 'confirmation',
      title: t('Delete Department'),
      message: t('This department and all associated data will be permanently deleted.'),
      subText: t('This action cannot be undone.'),
      icon: <PiTrashLight className="w-6 h-6 text-danger-600" />,
      variant: 'danger',
      confirmLabel: t('Delete'),
      showModal: true,
      onConfirm: async () => {
        if (!department?.id) return;
        const result = await dispatch(deleteDepartment(department.id) as any);
        dispatch(hideModal());
        if (result) history.push('/departments');
      },
    } as any));
  };

  const handleUploadDocument = async (files: File[]) => {
    setIsUploadingDocs(true);
    setUploadingDocCount(files.length);
    dispatch(hideModal());
    try {
      const formData = new FormData();
      for (const file of files) formData.append('files', file);

      const response = await apis.post(
        `/org-people/departments/${departmentId}/documents/bulk-upload`,
        formData
      );

      if (response?.data?.success) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: response.data.message || t('Documents uploaded successfully'),
          modalType: 'success',
          duration: 3000,
        }));
      }

      await loadDocuments();
      await loadDepartment();
      await loadFeed();
    } catch (err) {
      console.error('Error uploading document:', err);
      dispatch(showFeedbackModal({
        showModal: true,
        message: `${t('Upload failed')}: ${err instanceof Error ? err.message : t('Unknown error')}`,
        modalType: 'error',
        duration: 3000,
      }));
    } finally {
      setIsUploadingDocs(false);
      setUploadingDocCount(0);
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    try {
      await apis.delete(`/org-people/departments/${departmentId}/documents/${documentId}`);
      await loadDocuments();
    } catch (err) {
      console.error('Error deleting document:', err);
      dispatch(showFeedbackModal({
        showModal: true,
        message: `${t('Delete failed')}: ${err instanceof Error ? err.message : t('Unknown error')}`,
        modalType: 'error',
        duration: 3000,
      }));
    }
  };

  const handleBulkDeleteDocuments = async (documentIds: string[]) => {
    await apis.post(`/org-people/departments/${departmentId}/documents/bulk-delete`, { document_ids: documentIds });
    await loadDocuments();
    await loadDepartment();
  };

  const handleOpenDocumentUploadModal = () => {
    dispatch(showModal({
      type: 'uploadAttachment',
      message: t('Upload Department Document'),
      icon: <PiPlusCircleLight className="w-6 h-6" />,
      showModal: true,
      onConfirm: handleUploadDocument,
    }));
  };

  const TAB_LABELS: Record<typeof DEPARTMENT_TABS[number], string> = {
    Details: t('Details'),
    Feed: t('Feed'),
    Documents: t('Documents'),
  };
  const tabs: TabItem[] = DEPARTMENT_TABS.map((value) => ({ label: TAB_LABELS[value], value }));

  const breadcrumbs = [
    { name: t('Departments'), href: '/departments', current: false },
    { name: department?.name || t('Department'), href: `/departments/${departmentId}`, current: true },
  ];

  if (loading) {
    return (
      <DetailFrame breadcrumbs={breadcrumbs} showBackButton>
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex-shrink-0 w-full">
            <DetailTabBar tabs={tabs} activeTab="Details" onTabClick={() => {}} />
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main">
            <Skeleton variant="detail" surface={DEPARTMENT_SURFACE} />
          </div>
        </div>
      </DetailFrame>
    );
  }

  if (error || !department) {
    return (
      <DetailFrame breadcrumbs={breadcrumbs} showBackButton>
        <div className="text-center py-12">
          <p className="text-danger-600 dark:text-danger-400 mb-4">{error || t('Department not found')}</p>
          <Button onClick={() => history.push('/departments')}>
            <PiArrowLeftLight className="w-4 h-4 mr-2" />
            {t('Back to Departments')}
          </Button>
        </div>
      </DetailFrame>
    );
  }

  const members = department.members || [];

  return (
    /* Lets the Documents tab put a file in the reader pane without threading
       "open the reader" down as a prop — see `Shared/DocumentsTab`. */
    <OrgSurfaceProvider value={surfaceApi}>
    <DetailFrame
      breadcrumbs={breadcrumbs}
      dropdownActions={[
        {
          label: t('Manage Members'),
          icon: <PiUsersLight className="w-4 h-4" />,
          onClick: () => surfaceApi.openPane('access'),
        },
        {
          label: t('Activity log'),
          icon: <PiClockCounterClockwiseLight className="w-4 h-4" />,
          onClick: () => surfaceApi.openPane('activity'),
        },
        {
          label: t('Delete'),
          icon: <PiTrashLight className="w-4 h-4" />,
          onClick: handleDeleteDepartment,
        },
      ]}
      subscriptionActive
      showBackButton
      additionalHeaderElements={members.length > 0 ? (
        <CollaboratorStack
          collaborators={toAccessRows(members)}
          max={4}
          size="md"
          withAccessLabels={false}
          onClick={() => surfaceApi.openPane('access')}
          testId="department-header-access-stack"
        />
      ) : null}
    >
      <div className="flex-1 flex flex-col min-h-0">
        <div data-tour-id="dept-detail-tabs" className="flex-shrink-0 w-full">
          <DetailTabBar
            tabs={tabs}
            activeTab={activeTab}
            onTabClick={goToTab}
            actions={(
              <PaneRail
                panels={panels}
                ariaLabel={t('Panels')}
                labels="auto"
                items={DEPARTMENT_SURFACE.railItems({
                  t,
                  icons: {
                    context: <PiInfoLight />,
                    access: <PiUsersLight />,
                    activity: <PiClockCounterClockwiseLight />,
                  },
                  badges: { access: members.length },
                })}
              />
            )}
          />
        </div>

        <DetailPageSurface
          surface={DEPARTMENT_SURFACE}
          layoutApi={panels}
          panes={[
            {
              id: 'context',
              title: t('Context'),
              icon: <PiInfoLight />,
              body: (
                <OrgContextPane
                  record={department}
                  onOpenAccess={() => surfaceApi.openPane('access')}
                  onOpenDocuments={() => goToTab('Documents')}
                />
              ),
            },
            {
              id: 'access',
              title: t('Members'),
              icon: <PiUsersLight />,
              body: (
                <AccessPane
                  collaborators={toAccessRows(members)}
                  onManageAccess={handleManageAccess}
                  canManage
                  ownerLabel="Lead"
                  emptyText={t('No members yet.')}
                />
              ),
            },
            {
              id: 'activity',
              title: t('Activity'),
              icon: <PiClockCounterClockwiseLight />,
              body: () => (
                <PaneBody>
                  <AuditLogPanel
                    entityType="Department"
                    entityId={departmentId}
                    entityName={department.name}
                    onClose={() => panels.setVisible('activity', false)}
                  />
                </PaneBody>
              ),
            },
            {
              /* Opened by the Documents tab, never by the rail — see
                 `Shared/DocumentsTab`. */
              id: 'reader',
              title: readerContent?.title || t('Reader'),
              icon: <PiFileTextLight />,
              body: (
                <OrgReaderPane
                  content={readerContent}
                  onClose={() => {
                    panels.setVisible('reader', false);
                    setReaderContent(null);
                  }}
                />
              ),
            },
          ]}
        >
          {activeTab === 'Details' && (
            <div
              data-tour-id="dept-detail-content"
              className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main"
            >
              <div className="mx-auto w-full max-w-6xl px-6 py-8 space-y-8">
                <GlassCard
                  icon={<PiTextAlignLeftLight />}
                  title={t('Description')}
                  padding="lg"
                  actions={!isEditingDeptInfo ? (
                    <Button
                      variant="outline"
                      size="small"
                      onClick={() => setIsEditingDeptInfo(true)}
                      icon={<PiPencilLight className="h-3.5 w-3.5" />}
                    >
                      {t('Edit')}
                    </Button>
                  ) : undefined}
                >
                  {isEditingDeptInfo ? (
                    <div className="space-y-4">
                      <Input
                        type="text"
                        label={t('Department Name')}
                        placeholder={t('Enter department name')}
                        value={editedDeptFields.name || ''}
                        onChange={(e) => handleDeptFieldChange('name', e.target.value)}
                        required
                        className="w-full"
                      />
                      <Textarea
                        label={t('Description')}
                        placeholder={t('Enter department description')}
                        value={editedDeptFields.description || ''}
                        onChange={(e) => handleDeptFieldChange('description', e.target.value)}
                        className="w-full"
                        id="dept-description"
                      />
                      <Dropdown
                        label={t('Practice Areas')}
                        options={practiceAreaOptions}
                        value={(editedDeptFields.practice_area_ids || []).map(String)}
                        onChange={(vals) => handleDeptFieldChange('practice_area_ids', Array.isArray(vals) ? vals.map(Number) : [Number(vals)])}
                        multiple
                        fullWidth
                        placeholder={t('Select practice areas')}
                      />
                      <div className="flex justify-end gap-3 pt-3 border-t border-hairline-soft">
                        <Button variant="neutral" size="small" onClick={handleCancelEdit} disabled={isSaving}>
                          {t('Cancel')}
                        </Button>
                        <Button
                          variant="primary"
                          size="small"
                          onClick={handleSaveDepartment}
                          disabled={isSaving}
                          icon={isSaving ? <Spinner size="glyph" tone="current" /> : undefined}
                        >
                          {isSaving ? t('Saving...') : t('Save Changes')}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    department.description ? (
                      <p className="text-sm text-fg-body leading-relaxed whitespace-pre-wrap max-w-prose">
                        {department.description}
                      </p>
                    ) : (
                      <p className="text-sm text-fg-muted">{t('No description provided')}</p>
                    )
                  )}
                </GlassCard>

                <FieldGrid columns={4}>
                  <FactTile icon={<PiCircleDashedLight />} label={t('Status')}>
                    <Badge color={department.is_active ? 'green' : 'gray'}>
                      {department.is_active ? t('Active') : t('Inactive')}
                    </Badge>
                  </FactTile>
                  <FactTile icon={<PiUsersLight />} label={t('Members')} value={members.length} />
                  <FactTile icon={<PiFileTextLight />} label={t('Documents')} value={department.document_count || 0} />
                  <FactTile
                    icon={<PiCalendarBlankLight />}
                    label={t('Created')}
                    value={department.created_at ? formatDate(department.created_at) : undefined}
                  />
                </FieldGrid>

                {department.practice_areas && department.practice_areas.length > 0 && (
                  <GlassCard icon={<PiTagLight />} title={t('Practice Areas')}>
                    <div className="flex flex-wrap gap-2">
                      {department.practice_areas.map((pa) => (
                        <Badge key={pa.id} color="blue">{pa.name}</Badge>
                      ))}
                    </div>
                  </GlassCard>
                )}
              </div>
            </div>
          )}

          {activeTab === 'Feed' && (
            <div className="flex-1 min-h-0 flex flex-col px-4 pb-3">
              <ActivityFeed
                events={feed}
                loading={feedLoading}
                onRefresh={loadFeed}
                onPostEvent={handlePostFeedEvent}
                onCommentAdd={handleAddComment}
                currentUser={userInfo ? {
                  id: userInfo.UserID || 'current-user',
                  first_name: userInfo.FirstName || 'You',
                  last_name: userInfo.LastName || '',
                  email: userInfo.Email || '',
                } : undefined}
                members={members}
              />
            </div>
          )}

          {activeTab === 'Documents' && (
            <div className="flex-1 min-h-0 flex flex-col">
              <div className="flex-shrink-0 flex justify-end px-4 pt-3">
                <Button size="small" onClick={handleOpenDocumentUploadModal}>
                  <PiPlusCircleLight className="w-4 h-4 mr-1" />
                  {t('Add Document')}
                </Button>
              </div>
              <DocumentsTab
                domain="departments"
                resourceId={departmentId}
                documents={documents}
                loading={documentsLoading}
                isUploading={isUploadingDocs}
                uploadingCount={uploadingDocCount}
                onUpload={handleUploadDocument}
                onDelete={handleDeleteDocument}
                onBulkDelete={handleBulkDeleteDocuments}
                onRefresh={loadDocuments}
              />
            </div>
          )}
        </DetailPageSurface>
      </div>

      <FeatureTour
        steps={departmentDetailTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        continuous
        showProgress
        showSkipButton
        disableOverlay
      />
    </DetailFrame>
    </OrgSurfaceProvider>
  );
};

export default DepartmentDetail;
